type AuthEventMeta = Record<string, string | number | boolean | null | undefined>;

const TRACE_KEY = "auth_debug_trace_id";
const SEQUENCE_KEY = "auth_debug_sequence";
const MAX_BUFFERED_EVENTS = 50;

// In-memory fallback used when sessionStorage is unavailable (privacy mode, quota,
// disabled storage, iframe restrictions). Diagnostics must never affect app boot.
const memoryState: { traceId: string | null; sequence: number } = { traceId: null, sequence: 0 };
const bufferedEvents: Array<{ action: string; payload: AuthEventMeta }> = [];

function readStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // Fall back to memory only.
  }
}

function traceId(): string {
  let value = readStorage(TRACE_KEY);
  if (!value) {
    value = memoryState.traceId || `auth-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    writeStorage(TRACE_KEY, value);
  }
  memoryState.traceId = value;
  return value;
}

function nextSequence(): number {
  const stored = Number(readStorage(SEQUENCE_KEY) || "0");
  const current = isFinite(stored) && stored > memoryState.sequence ? stored : memoryState.sequence;
  const next = current + 1;
  memoryState.sequence = next;
  writeStorage(SEQUENCE_KEY, String(next));
  return next;
}

function elapsedMs(): number | null {
  try {
    return typeof performance !== "undefined" && typeof performance.now === "function"
      ? Math.round(performance.now())
      : null;
  } catch (e) {
    return null;
  }
}

function safePathname(pathname: string): string {
  // React Router receives paths without its /auth basename; browser navigation retains it.
  return /^\/(?:auth\/)?[A-Za-z0-9/_-]*$/.test(pathname) ? pathname : "[invalid-path]";
}

function currentPath(): string {
  try {
    return safePathname(window.location.pathname);
  } catch (e) {
    return "[invalid-path]";
  }
}

function remember(action: string, payload: AuthEventMeta): void {
  bufferedEvents.push({ action, payload });
  if (bufferedEvents.length > MAX_BUFFERED_EVENTS) {
    bufferedEvents.splice(0, bufferedEvents.length - MAX_BUFFERED_EVENTS);
  }
}

/** Snapshot of recent events for tests/debugging. Never transmitted. */
export function getBufferedAuthEvents(): ReadonlyArray<{ action: string; payload: AuthEventMeta }> {
  return bufferedEvents.slice();
}

export function trackAuthEvent(action: string, meta: AuthEventMeta = {}): void {
  try {
    if (typeof window === "undefined") return;
    const payload: AuthEventMeta = {
      trace_id: traceId(),
      sequence: nextSequence(),
      elapsed_ms: elapsedMs(),
      path: currentPath(),
      ...meta,
    };
    remember(action, payload);
    // Keep the same intentionally sanitized event payload available during rollout.
    if (typeof console !== "undefined" && typeof console.info === "function") {
      console.info(`[auth.telemetry] ${action}`, payload);
    }
  } catch (e) {
    // Diagnostics are best effort and must never break the auth UI.
  }
}

export function observeAuthRoute(pathname: string, event: "page_viewed" | "route_changed"): void {
  trackAuthEvent(event, { route: safePathname(pathname) });
}

export function getSafeOAuthContext(): AuthEventMeta {
  try {
    const query = new URLSearchParams(window.location.search);
    return {
      has_request_id: query.has("request_id"),
      has_client_id: query.has("client_id"),
      has_redirect_uri: query.has("redirect_uri"),
      has_oauth_error: query.has("error"),
      has_oauth_error_description: query.has("error_description"),
    };
  } catch (e) {
    return {};
  }
}

export function installAuthErrorObservability(): () => void {
  const onError = (event: ErrorEvent) => {
    trackAuthEvent("client_error", {
      error_type: "window_error",
      has_message: Boolean(event && event.message),
    });
  };
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event && event.reason;
    trackAuthEvent("client_error", {
      error_type: "unhandled_rejection",
      rejection_kind: reason instanceof Error ? "error" : "non_error",
    });
  };

  try {
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
  } catch (e) {
    return () => {};
  }
  return () => {
    try {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    } catch (e) {
      // ignore
    }
  };
}
